/**
 * Shopify cart drawer — bal-com-v4 aside--cart layout, backed by /cart.js API.
 */
(function () {
  "use strict";

  const FREE_SHIP_THRESHOLD = 60;
  const cartRoot = document.querySelector("[data-cart-root]");
  const emptyEl = document.querySelector("[data-cart-empty]");
  const filledEl = document.querySelector("[data-cart-filled]");
  const footerEl = document.querySelector("[data-cart-footer]");
  const countEl = document.querySelector(".header__cart-count");
  const cartBtn = document.querySelector(".header__cart-btn");
  const cartOverlay = document.getElementById("overlay-cart");

  if (!cartRoot || !emptyEl || !filledEl) return;

  function cartRootUrl(path) {
    const base = window.Shopify?.routes?.root || "/";
    return `${base}${path.replace(/^\//, "")}`;
  }

  function esc(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  /** "BALANCE Complex" → "Balance Complex" (first letter of each word only) */
  function titleCaseWords(str) {
    return String(str || "")
      .toLowerCase()
      .replace(/(^|[\s\-_/])([a-z])/g, function (_, boundary, letter) {
        return boundary + letter.toUpperCase();
      });
  }

  function fmtMoney(cents) {
    const n = Number(cents) / 100;
    return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
  }

  function heroUrlForHandle(handle) {
    if (!handle) return "";
    if (window.FB_PDP_HERO_URLS && window.FB_PDP_HERO_URLS[handle]) {
      return window.FB_PDP_HERO_URLS[handle];
    }
    if (window.FB_CROSS_SELL_IMAGES && window.FB_CROSS_SELL_IMAGES[handle]) {
      return window.FB_CROSS_SELL_IMAGES[handle];
    }
    return "";
  }

  function normalizeImageUrl(url) {
    if (!url) return "";
    if (url.indexOf("//") === 0) return "https:" + url;
    return url;
  }

  function sizedImageUrl(rawUrl) {
    const baseUrl = normalizeImageUrl(rawUrl);
    if (!baseUrl) return { src: "", srcset: "" };
    const join = baseUrl.indexOf("?") >= 0 ? "&" : "?";
    return {
      src: baseUrl + join + "width=96",
      srcset: baseUrl + join + "width=96 96w, " + baseUrl + join + "width=192 192w",
    };
  }

  function lineImageUrl(line) {
    const handle = line.handle || "";
    let raw = line.image || heroUrlForHandle(handle) || "";
    if (!raw && line.featured_image) {
      raw = typeof line.featured_image === "string" ? line.featured_image : line.featured_image.url || "";
    }
    return sizedImageUrl(raw);
  }

  function purchaseLabel(line) {
    if (line.selling_plan_allocation?.selling_plan?.name) {
      return line.selling_plan_allocation.selling_plan.name;
    }
    if (line.variant_title && line.variant_title !== "Default Title") {
      return line.variant_title;
    }
    return "One Time Purchase";
  }

  function openDrawer() {
    if (!cartOverlay) return;
    cartOverlay.classList.add("expanded");
    document.documentElement.style.overflow = "hidden";
  }

  function isCartPageHref(href) {
    if (!href || href.charAt(0) === "#") return false;
    try {
      const url = new URL(href, window.location.origin);
      // Match /cart (optional locale prefix), but not /cart/add or /checkout
      return /\/cart\/?$/.test(url.pathname);
    } catch (err) {
      return false;
    }
  }

  async function fetchCart() {
    const res = await fetch(cartRootUrl("/cart.js"), { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error("Could not load cart");
    return res.json();
  }

  async function addToCart(id, quantity) {
    const res = await fetch(cartRootUrl("/cart/add.js"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [{ id: Number(id), quantity: Math.max(1, Number(quantity) || 1) }],
      }),
    });
    if (!res.ok) {
      let message = "Could not add to cart";
      try {
        const err = await res.json();
        message = err.description || err.message || message;
      } catch (e) {
        /* ignore parse errors */
      }
      throw new Error(message);
    }
    return res.json();
  }

  async function addFromForm(form) {
    if (!form) return;
    const idInput = form.querySelector('[name="id"]');
    const qtyInput = form.querySelector('[name="quantity"]');
    const id = idInput && idInput.value;
    if (!id) throw new Error("Choose a product option first");
    await addToCart(id, qtyInput ? qtyInput.value : 1);
    await refresh();
    openDrawer();
    document.dispatchEvent(new CustomEvent("v4:cart-updated"));
  }

  // Product forms normally navigate to /cart — keep shoppers in the drawer instead
  function bindProductForms() {
    document.querySelectorAll("#ib-pdp-form, form[action*='/cart/add']").forEach(function (form) {
      if (form.dataset.v4CartBound === "true") return;
      form.dataset.v4CartBound = "true";
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        var submitBtn = form.querySelector("[data-atc], button[type='submit']");
        if (submitBtn && submitBtn.disabled) return;
        var prevLabel = submitBtn ? submitBtn.textContent : "";
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = "Adding…";
        }
        addFromForm(form)
          .catch(function (err) {
            console.error(err);
            window.alert(err.message || "Could not add to cart");
          })
          .finally(function () {
            if (!submitBtn) return;
            submitBtn.disabled = false;
            submitBtn.textContent = prevLabel || "Add to cart";
          });
      });
    });
  }

  function renderLine(line) {
    const img = lineImageUrl(line);
    const productName = titleCaseWords(line.product_title);
    const alt = esc(productName);
    const imgTag = img.src
      ? `<img src="${esc(img.src)}" srcset="${esc(img.srcset)}" sizes="96px" alt="${alt}" width="96" height="96" decoding="async" loading="lazy" class="cart-line__img" style="aspect-ratio:1/1" />`
      : `<span class="cart-line__img cart-line__img--fallback" aria-hidden="true"></span>`;

    return (
      `<li class="cart-line" data-line-key="${esc(line.key)}">` +
      `<a href="${esc(line.url)}" class="cart-line__img-link" data-overlay-close="overlay-cart">` +
      imgTag +
      `</a>` +
      `<div class="cart-line__info">` +
      `<p class="cart-line__header"><a href="${esc(line.url)}" data-overlay-close="overlay-cart">${esc(productName)}</a></p>` +
      `<ul><li>${esc(purchaseLabel(line))}</li></ul>` +
      `<div class="cart-line__item-update">` +
      `<div class="cart-line__qty-pill cart-line__quantity" role="group" aria-label="Quantity">` +
      `<button type="button" class="cart-line__qty-btn" data-cart-dec="${esc(line.key)}" aria-label="${line.quantity <= 1 ? "Remove from cart" : "Decrease quantity"}"><span>− </span></button>` +
      `<p class="cart-line__qty-num" aria-live="polite">${line.quantity}</p>` +
      `<button type="button" class="cart-line__qty-btn" data-cart-inc="${esc(line.key)}" aria-label="Increase quantity"><span>+</span></button>` +
      `</div>` +
      `<div class="cart-line__price"><span><div>${fmtMoney(line.final_line_price)}</div></span></div>` +
      `</div></div></li>`
    );
  }

  function render(cart) {
    const items = cart.items || [];
    const hasItems = items.length > 0;
    const qty = items.reduce(function (sum, line) {
      return sum + line.quantity;
    }, 0);
    const total = Number(cart.total_price || 0) / 100;
    const remaining = Math.max(0, FREE_SHIP_THRESHOLD - total);
    const progressPct = Math.min(100, (total / FREE_SHIP_THRESHOLD) * 100);

    emptyEl.hidden = hasItems;
    filledEl.hidden = !hasItems;
    if (footerEl) footerEl.hidden = !hasItems;

    if (countEl) {
      countEl.textContent = String(qty);
      countEl.dataset.empty = qty === 0 ? "true" : "false";
    }
    if (cartBtn) {
      cartBtn.setAttribute("aria-label", qty === 1 ? "Cart, 1 item" : "Cart, " + qty + " items");
    }

    if (!hasItems) {
      filledEl.innerHTML = "";
      if (footerEl) footerEl.innerHTML = "";
      return;
    }

    const freeshipText =
      remaining > 0
        ? '<span class="cart-freeship__accent">Free shipping</span> is only <span class="cart-freeship__accent">' +
          fmtMoney(remaining * 100) +
          "</span> away"
        : '<span class="cart-freeship__accent">You unlocked free shipping ✿</span>';

    filledEl.innerHTML =
      `<div class="cart-freeship">` +
      `<p class="cart-freeship__text">${freeshipText}</p>` +
      `<div class="cart-freeship__bar" role="progressbar" aria-valuenow="${Math.round(progressPct)}" aria-valuemin="0" aria-valuemax="100">` +
      `<div class="cart-freeship__bar-fill" style="width:${progressPct}%"></div>` +
      `</div></div>` +
      `<div class="cart-details">` +
      `<p class="cart-main__header">Your Cart:</p>` +
      `<p id="cart-lines" class="sr-only">Line items</p>` +
      `<div><ul aria-labelledby="cart-lines">` +
      items.map(renderLine).join("") +
      `</ul></div></div>`;

    const checkoutUrl = cart.checkout_url || cartRootUrl("/checkout");

    if (footerEl) {
      footerEl.innerHTML =
        `<div class="cart-checkout-actions">` +
        `<p class="cart-checkout-actions__heading">Order Summary</p>` +
        `<div class="cart-discount-toggle">` +
        `<button type="button" class="cart-discount-toggle__btn" aria-expanded="false" data-cart-discount-toggle>` +
        `<span>Have a discount or gift card?</span><span aria-hidden="true">+</span>` +
        `</button>` +
        `<div class="cart-discount-toggle__panel" hidden>` +
        `<p class="cart-discount-toggle__note">Enter your code at checkout.</p>` +
        `</div></div>` +
        `<div class="cart-checkout-actions__main">` +
        `<div class="cart-checkout-actions__subtotal">` +
        `<span class="cart-checkout-actions__subtotal-label">Subtotal</span>` +
        `<span class="cart-checkout-actions__subtotal-amount">${fmtMoney(cart.total_price)}</span>` +
        `</div>` +
        `<p class="cart-checkout-actions__tax">Tax and shipping calculated at checkout</p>` +
        `</div>` +
        `<a class="cart-checkout-cta" href="${esc(checkoutUrl)}">Proceed to Checkout</a>` +
        `<p class="cart-checkout-actions__guarantee">90 day money-back guarantee</p>` +
        `</div>`;
    }
  }

  async function refresh() {
    try {
      render(await fetchCart());
    } catch (err) {
      console.error(err);
    }
  }

  async function changeLine(key, quantity) {
    const res = await fetch(cartRootUrl("/cart/change.js"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
    if (!res.ok) throw new Error("Could not update cart");
    render(await res.json());
  }

  cartRoot.addEventListener("click", function (e) {
    const discountBtn = e.target.closest("[data-cart-discount-toggle]");
    if (discountBtn) {
      const panel = discountBtn.parentElement?.querySelector(".cart-discount-toggle__panel");
      const open = discountBtn.getAttribute("aria-expanded") === "true";
      discountBtn.setAttribute("aria-expanded", open ? "false" : "true");
      if (panel) panel.hidden = open;
      return;
    }

    const target = e.target.closest("[data-cart-inc], [data-cart-dec]");
    if (!target) return;
    e.preventDefault();
    const key = target.getAttribute("data-cart-inc") || target.getAttribute("data-cart-dec");
    if (!key) return;

    const row = target.closest(".cart-line");
    const qtyEl = row?.querySelector(".cart-line__qty-num");
    const current = Number(qtyEl?.textContent || 1);
    const nextQty = target.hasAttribute("data-cart-inc") ? current + 1 : current - 1;
    changeLine(key, Math.max(0, nextQty));
  });

  document.addEventListener("v4:cart-updated", refresh);

  cartBtn?.addEventListener("click", function () {
    refresh();
    openDrawer();
  });

  // Footer "Cart" and other /cart links → side drawer (not the full cart page)
  document.addEventListener("click", function (e) {
    const link = e.target.closest("a[href]");
    if (!link) return;
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (link.target === "_blank") return;
    if (!isCartPageHref(link.getAttribute("href"))) return;
    e.preventDefault();
    refresh();
    openDrawer();
  });

  bindProductForms();

  window.V4ShopifyCart = {
    openDrawer: openDrawer,
    refresh: refresh,
    addToCart: addToCart,
    addFromForm: addFromForm,
  };

  refresh();
})();
